import { useState } from "react";
import { ChevronDown, ChevronUp, Lightbulb, Users, Tag, Wand2, Copy, Check } from "lucide-react";
import { clsx } from "clsx";
import type { IdeaBrief } from "@erc/shared";
import { buildFullImagePrompt, buildBriefUserPrompt } from "../../lib/designPrompt";

interface IdeaBriefsProps {
  briefs: IdeaBrief[];
  onCreateDraft?: (brief: IdeaBrief) => void;
  onUseBrief?: (brief: IdeaBrief) => void;
  referenceImageUrl?: string;
}

interface BriefCardProps {
  brief: IdeaBrief;
  index: number;
  onCreateDraft?: (brief: IdeaBrief) => void;
  onUseBrief?: (brief: IdeaBrief) => void;
  referenceImageUrl?: string;
}

// TR (ana) + EN (ikincil) satırı
function TrEn({ tr, en }: { tr?: string; en?: string }) {
  const trText = (tr || en || "").trim();
  if (!trText) return null;
  return (
    <>
      <p className="text-sm leading-relaxed text-gray-700">{trText}</p>
      {en && tr && en.trim() !== tr.trim() && (
        <p className="mt-0.5 text-xs italic leading-relaxed text-gray-400">{en}</p>
      )}
    </>
  );
}

function BriefCard({ brief, index, onCreateDraft, onUseBrief, referenceImageUrl }: BriefCardProps) {
  const [expanded, setExpanded] = useState(index === 0);
  const [copied, setCopied] = useState(false);
  const t = brief.translations ?? {};

  const copyBriefPrompt = () => {
    // Self-contained (metin-tabanli) tam prompt — brief'ler icin "özgün" kademe uygundur
    const full = buildFullImagePrompt(buildBriefUserPrompt(brief), "original", referenceImageUrl);
    navigator.clipboard.writeText(full).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => { /* ignore */ });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
            {index + 1}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{t.title || brief.title}</p>
            <p className="text-xs text-gray-500">{t.targetAudience || brief.targetAudience}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          {/* Concept */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Konsept
              </span>
            </div>
            <TrEn tr={t.concept} en={brief.concept} />
          </div>

          {/* Style guide */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Stil Rehberi
            </p>
            <TrEn tr={t.styleGuide} en={brief.styleGuide} />
          </div>

          {/* Target audience */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Users className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Hedef Kitle
              </span>
            </div>
            <TrEn tr={t.targetAudience} en={brief.targetAudience} />
          </div>

          {/* Color palette */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Renk Paleti
            </p>
            <div className="flex items-center gap-2">
              {brief.colorPalette.map((color) => (
                <div
                  key={color}
                  className="flex flex-col items-center gap-1"
                >
                  <div
                    className="h-8 w-8 rounded-full border border-gray-200 shadow-sm"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-[10px] text-gray-500 font-mono">{color}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Keywords */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Tag className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Anahtar Kelimeler
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {brief.keywords.map((kw) => (
                <span key={kw} className="tag-pill">
                  {kw}
                </span>
              ))}
            </div>
          </div>

          {/* Inspiration notes */}
          {(t.inspirationNotes || brief.inspirationNotes) && (
            <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5">
              <p className="text-xs font-semibold text-amber-700 mb-1">Not</p>
              <p className="text-sm text-amber-700">{t.inspirationNotes || brief.inspirationNotes}</p>
              {brief.inspirationNotes && t.inspirationNotes && brief.inspirationNotes !== t.inspirationNotes && (
                <p className="mt-0.5 text-xs italic text-amber-600/70">{brief.inspirationNotes}</p>
              )}
            </div>
          )}

          {/* CTA'lar */}
          <div className="flex flex-col gap-2 sm:flex-row">
            {onUseBrief && (
              <button
                onClick={() => onUseBrief(brief)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-fuchsia-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-fuchsia-700 transition-colors"
              >
                <Wand2 className="h-4 w-4" /> Bu Brief ile Tasarla
              </button>
            )}
            {onCreateDraft && (
              <button
                onClick={() => onCreateDraft(brief)}
                className={clsx(
                  "flex-1 rounded-lg border border-indigo-200 bg-white px-4 py-2.5 text-sm font-medium text-indigo-700",
                  "hover:bg-indigo-50 transition-colors"
                )}
              >
                Listing Oluştur
              </button>
            )}
            <button
              onClick={copyBriefPrompt}
              title="Bu brief'in tam görsel üretim prompt'unu kopyala (başka bir araçta kullan)"
              className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              {copied ? "Kopyalandı" : "Prompt Kopyala"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function IdeaBriefs({ briefs, onCreateDraft, onUseBrief, referenceImageUrl }: IdeaBriefsProps) {
  return (
    <div className="space-y-3">
      {briefs.map((brief, index) => (
        <BriefCard
          key={brief.id}
          brief={brief}
          index={index}
          onCreateDraft={onCreateDraft}
          onUseBrief={onUseBrief}
          referenceImageUrl={referenceImageUrl}
        />
      ))}
    </div>
  );
}
